/**
 * CodeDiff — before/after code block with line numbers, gutter markers,
 * and highlighted target line.
 */

import type { FixContextWindow } from "../../api/fix";

interface CodeDiffProps {
  context: FixContextWindow;
  fileName: string;
  lineNumber: number;
}

export default function CodeDiff({ context, fileName, lineNumber }: CodeDiffProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ color: "var(--gray-11)", fontSize: 13, fontFamily: "monospace" }}>
        {fileName} : {lineNumber}
      </div>

      <div>
        <div style={sectionHeaderStyle("#f44747")}>BEFORE:</div>
        <pre style={codeBlockStyle}>
          {context.before_lines.map(([ln, text], i) => {
            const isTarget = i === context.target_idx;
            return (
              <div
                key={`before-${ln}`}
                style={{
                  background: isTarget ? "#3a1d1d" : "transparent",
                  display: "flex",
                }}
              >
                <span style={gutterStyle}>
                  {isTarget ? ">" : " "} {String(ln).padStart(4, " ")} |{" "}
                </span>
                <span>{text}</span>
              </div>
            );
          })}
        </pre>
      </div>

      <div>
        <div style={sectionHeaderStyle("#4ec9b0")}>AFTER:</div>
        {context.stale ? (
          <pre style={codeBlockStyle}>
            <div style={{ color: "var(--gray-9)", padding: "8px 12px" }}>
              (stale) {context.stale_reason}
            </div>
          </pre>
        ) : (
          <pre style={codeBlockStyle}>
            {context.after_lines.map(([ln, text], i) => {
              const isTarget = i === context.target_idx;
              return (
                <div
                  key={`after-${ln}`}
                  style={{
                    background: isTarget ? "#1d3a1d" : "transparent",
                    display: "flex",
                  }}
                >
                  <span style={gutterStyle}>
                    {isTarget ? ">" : " "} {String(ln).padStart(4, " ")} |{" "}
                  </span>
                  <span>{text}</span>
                </div>
              );
            })}
          </pre>
        )}
      </div>
    </div>
  );
}

const sectionHeaderStyle = (color: string): React.CSSProperties => ({
  fontWeight: 700,
  fontSize: 13,
  color,
  marginBottom: 4,
  fontFamily: "Helvetica, Arial, sans-serif",
});

const codeBlockStyle: React.CSSProperties = {
  margin: 0,
  padding: "6px 0",
  background: "#2d2d2d",
  borderRadius: 4,
  fontFamily: "Menlo, Consolas, 'Courier New', monospace",
  fontSize: 12,
  lineHeight: 1.6,
  color: "#d4d4d4",
  overflow: "auto",
};

const gutterStyle: React.CSSProperties = {
  color: "#6a737d",
  userSelect: "none",
  whiteSpace: "pre",
};
